import differenceInDays from 'date-fns/differenceInDays'
import format from 'date-fns/format'
import isAfter from 'date-fns/isAfter'
import isBefore from 'date-fns/isBefore'

import {
  CustomInvoiceAdminDto,
  CustomOrderAdminDto,
  InvoiceAdminDto,
  LocationPetProfileAdminDto,
  OrderAdminDto,
  OrderClientDto,
} from 'service-api/src/types/api'
import { isEmpty } from './common'
import { getInvoiceDates } from './invoices'
import { i18WithDefault as t } from './locales'
import { joinPetNamesFromProfile } from './pets'

export const isOrderConfirmed = (order?: OrderClientDto | OrderAdminDto) =>
  order?.orderStatus === 'CONFIRMED' || order?.orderStatus === 'PARTIALLY_CANCELED'

export const getOrderDates = (
  order: OrderClientDto | OrderAdminDto
): { minDate: Date | null; maxDate: Date | null } => {
  let minDate: Date | null = null,
    maxDate: Date | null = null
  order.invoices?.forEach((invoice) => {
    const { startDate, endDate } = getInvoiceDates(invoice)
    if (!minDate || isBefore(startDate, minDate)) {
      minDate = startDate
    }
    if (!maxDate || isAfter(endDate, maxDate)) {
      maxDate = endDate
    }
  })
  return { minDate, maxDate }
}

export const getServiceDatesText = (order: OrderClientDto, options = {}) => {
  const mergedOptions = { fullYear: false, fullPeriod: false, fullYearLast: false, ...options }

  const { minDate, maxDate } = getOrderDates(order)

  return getServiceDateTextFormat(
    minDate,
    maxDate,
    order.primaryServiceType?.serviceType?.name,
    order.invoices?.length,
    mergedOptions
  )
}

export const getServiceDateTextFormat = (
  minDate: Date | null,
  maxDate: Date | null,
  serviceName: string | undefined,
  invoicesCnt: number = 0,
  options = {}
) => {
  const mergedOptions = { fullYear: false, fullPeriod: false, fullYearLast: false, ...options }

  if (!minDate || !maxDate) {
    return t('global.label.N/A')
  }
  if (serviceName === 'daycare') {
    return invoicesCnt === 1 ? format(minDate, 'MMM d, yyyy') : t('global.label.many')
  }

  if (differenceInDays(maxDate, minDate) <= 1 && !mergedOptions.fullPeriod) {
    return format(minDate, 'MMM d, yyyy')
  } else {
    return `${format(minDate, `MMM d${mergedOptions.fullYear ? ' yyyy' : ''}`)} - ${format(
      maxDate,
      `MMM d${mergedOptions.fullYear || mergedOptions.fullYearLast ? ' yyyy' : ''}`
    )}`
  }
}

export const getReservationSummary = (invoice: CustomInvoiceAdminDto) => {
  return (invoice.items || []).map((item) => `${item.qty} ${item.displayName}`).join(', ')
}

export const orderPetProfiles = (order?: OrderAdminDto) => {
  if (!order) {
    return []
  }
  return (
    order.pets
      ?.map((p) =>
        p.locationPetProfile
          ? p.locationPetProfile
          : ({ displayName: p.displayName, id: p.id } as LocationPetProfileAdminDto)
      )
      ?.filter(Boolean) || []
  )
}

export const orderFormatPetNames = (order?: OrderAdminDto) => {
  return joinPetNamesFromProfile(orderPetProfiles(order))
}

export const orderPetNamesOwner = (order?: OrderAdminDto, petsToExclude?: string[]) => {
  if (!order) {
    return []
  }
  const pets = orderPetProfiles(order)
  return pets.reduce((acc, pet) => {
    if (petsToExclude?.includes(pet.id || '')) {
      return acc
    }
    const lastName = order.locationUserProfile?.lastName || ''
    acc.push(`${pet.displayName} ${lastName}`.trim())
    return acc
  }, [] as string[])
}

export const getOrderPrimaryServiceTypeName = (order?: OrderAdminDto) => order?.primaryServiceType?.serviceType?.name

export const getOrderPrimaryServiceTypeDisplayName = (order?: OrderAdminDto) =>
  order?.primaryServiceType?.serviceType?.displayName

// Gets the primary invoice that matchs the primary service type of the order
// On some cases (always?) the first invoice in the order is not the right one,
// for example TemperTests are PRIMARY, is on first place but is not the
// order's primary service type
export const getPrimaryInvoice = (order?: OrderAdminDto | CustomOrderAdminDto) => {
  const primaryStId = order?.primaryServiceType?.serviceType?.id || ''
  return order?.invoices?.find(
    (invoice) =>
      (invoice.items?.findIndex(
        (item) =>
          item.offerType === 'PRIMARY' &&
          (item.locationServiceType?.serviceTypeId === primaryStId ||
            item.locationServiceType?.serviceType?.id === primaryStId)
      ) ?? -1) > -1
  )
}

export const isOvernightFromOrder = (order?: OrderAdminDto) => !!order?.primaryServiceType?.serviceType?.overnight

export const isVisitOrder = (order?: OrderAdminDto | OrderClientDto) =>
  !!order?.primaryServiceType?.serviceType?.isVisit

export const isAdyenEnabled = (order?: OrderAdminDto) => order?.location?.payFacConfig?.type === 'ADYEN'

export const isNewReservationOrder = (order?: OrderAdminDto | CustomOrderAdminDto): boolean =>
  order?.status === 'PENDING'

export const getOrderStatus = ({
  invoice,
  order,
  overnight,
}: {
  invoice?: InvoiceAdminDto
  order?: OrderAdminDto
  overnight?: boolean
}): string | null => {
  const getStatus = (status?: string) => {
    switch (status) {
      case 'PENDING':
        return t('global.label.pending')
      case 'PENDING_PAYMENT':
        return t('global.label.pending')
      case 'CONFIRMED':
        return t('global.label.confirmed')
      case 'FAILED':
        return t('global.label.declined')
      case 'CANCELED':
        return t('global.label.canceled')
      case 'PARTIALLY_CANCELED':
        return t('global.label.partiallyCanceled')
      default:
        return null
    }
  }

  if (overnight === undefined) {
    return invoice ? getStatus(invoice?.invoiceStatus) : getStatus(order?.orderStatus)
  } else if (overnight) {
    return getStatus(invoice?.order?.orderStatus)
  } else {
    return getStatus(invoice?.invoiceStatus)
  }
}

export const getPrimaryInvoices = (
  order: OrderAdminDto | CustomOrderAdminDto
): InvoiceAdminDto[] | CustomInvoiceAdminDto[] => {
  return (
    order.invoices?.filter(
      (invoice) => !invoice.isInStay && invoice.items?.some((item) => item.offerType === 'PRIMARY')
    ) || []
  )
}

export const getCustomerDetails = (order?: OrderAdminDto | OrderClientDto) => {
  const name = `${order?.locationUserProfile?.firstName || order?.userData?.[0]?.firstName || ''} ${
    order?.locationUserProfile?.lastName || order?.userData?.[0]?.lastName || ''
  }`
  const streetAddress1 =
    order?.locationUserProfile?.streetAddress || order?.userData?.[0]?.data?.mailingAddress?.streetAddress || ''
  const streetAddress2 =
    order?.locationUserProfile?.streetAddress2 || order?.userData?.[0]?.data?.mailingAddress?.streetAddress2 || ''
  const city = order?.locationUserProfile?.city || order?.userData?.[0]?.data?.mailingAddress?.city || ''
  const state = order?.locationUserProfile?.state || order?.userData?.[0]?.data?.mailingAddress?.state || ''
  const zipCode = order?.locationUserProfile?.zipCode || order?.userData?.[0]?.data?.mailingAddress?.zipCode || ''
  const phone = order?.locationUserProfile?.phone || order?.userData?.[0]?.data?.mailingAddress?.phone || ''
  const address = [streetAddress1, streetAddress2, city, state, zipCode].filter((item) => !isEmpty(item)).join(' ')
  const email = order?.locationUserProfile?.email || order?.userData?.[0]?.email || ''
  return { name, address, phone, zipCode, email }
}
